import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography, Pagination,
} from '@mui/material';
import { Add as AddIcon, Visibility } from '@mui/icons-material';
import { SupplierBillService } from '../../services/supplierBill.service';
import { SupplierService } from '../../services/supplier.service';
import { loadSuppliersSafely } from '../../utils/supplierAccess';
import { SupplierBill, SUPPLIER_BILL_STATUS_COLORS, isOnApprovalHold, approvalHoldLabel, APPROVAL_HOLD_TIP } from '../../interfaces/procurement/supplierBill.interface';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { formatFc, isForeignCurrency } from '../../utils/fcDocuments'; // G5a polish

const STATUS_COLORS = SUPPLIER_BILL_STATUS_COLORS;

export const SupplierBillList: React.FC = () => {
  const navigate = useNavigate();
  const canCreate = usePermission(PERMISSIONS.SUPPLIER_BILL_CREATE); // creating a bill is SUPPLIER_BILL_CREATE server-side
  const toast = useToast();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [bills, setBills] = useState<SupplierBill[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [loadingBills, setLoadingBills] = useState(true);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

  useEffect(() => {
    loadSuppliersSafely(() => SupplierService.listByInstitution(0, 500, 'ALL'))
      .then(r => { setSuppliers(r.suppliers); if (r.error) toast.showError(r.error); })
      .finally(() => setLoadingSuppliers(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBills = async (supplierId: string, page = 0) => {
    setLoadingBills(true);
    try {
      const response = supplierId
        ? await SupplierBillService.listBySupplier(supplierId, page, 10)
        : await SupplierBillService.listByInstitution(page, 10);
      setBills(response.content);
      setPageInfo({ currentPage: response.number, totalPages: response.totalPages, totalElements: response.totalElements });
    } catch (e) {
      toast.showApiError(e, 'Failed to load supplier bills');
    } finally {
      setLoadingBills(false);
    }
  };

  useEffect(() => {
    fetchBills('', 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSupplierChange = (supplierId: string) => {
    setSelectedSupplierId(supplierId);
    setBills([]);
    setPageInfo({ currentPage: 0, totalPages: 0, totalElements: 0 });
    fetchBills(supplierId, 0);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    fetchBills(selectedSupplierId, page - 1);
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.billList.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Supplier Bills</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canCreate && (
            <Button data-help="procurement.billList.newService" variant="outlined" onClick={() => navigate('/procurement/supplier-bills/new-service')}>
              New Service Bill (no PO)
            </Button>
          )}
          {canCreate && (
            <Button data-help="procurement.billList.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/procurement/supplier-bills/new')}>
              New Supplier Bill
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: '1 1 260px', maxWidth: 400 }} data-help="procurement.billList.supplierFilter">
        <SearchableSelect<Supplier>
          options={suppliers}
          value={selectedSupplierId}
          onChange={id => handleSupplierChange(id)}
          getOptionLabel={s => s.name}
          label="Supplier"
          size="medium"
          placeholder="All suppliers"
          disabled={loadingSuppliers}
        />
        </Box>
      </Box>

      {loadingBills ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>Total: {pageInfo.totalElements}</Typography>
          <TableContainer component={Paper} data-help-section="procurement.billList.table">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell data-help="procurement.billList.col.number">Bill Number</TableCell>
                  <TableCell data-help="procurement.billList.col.supplier">Supplier</TableCell>
                  <TableCell data-help="procurement.billList.col.ref">Supplier Invoice Ref</TableCell>
                  <TableCell data-help="procurement.billList.col.po">PO Number</TableCell>
                  <TableCell data-help="procurement.billList.col.date">Bill Date</TableCell>
                  <TableCell data-help="procurement.billList.col.due">Due Date</TableCell>
                  <TableCell data-help="procurement.billList.col.status">Status</TableCell>
                  <TableCell data-help="procurement.billList.col.total">Total</TableCell>
                  <TableCell data-help="procurement.billList.col.paid">Paid</TableCell>
                  <TableCell data-help="procurement.billList.col.actions">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bills.map(bill => (
                  <TableRow key={bill.id}>
                    <TableCell>{bill.billNumber}</TableCell>
                    <TableCell>{bill.supplierName ?? '—'}</TableCell>
                    <TableCell>{bill.supplierInvoiceRef ?? '-'}</TableCell>
                    <TableCell>{bill.purchaseOrderNumber ?? '—'}</TableCell>
                    <TableCell>{formatDate(bill.billDate)}</TableCell>
                    <TableCell>{formatDate(bill.dueDate)}</TableCell>
                    <TableCell>
                      {isOnApprovalHold(bill) ? (
                        <Tooltip title={APPROVAL_HOLD_TIP}>
                          <Chip data-help="procurement.billList.hold" label={approvalHoldLabel(bill)} color="warning" size="small" />
                        </Tooltip>
                      ) : (
                        <Chip data-help="procurement.billList.status" label={humanizeEnum(bill.status)} color={STATUS_COLORS[bill.status]} size="small" />
                      )}
                    </TableCell>
                    <TableCell>{formatMoney(bill.totalAmount)}
                    {isForeignCurrency(bill.currencyCode) && bill.foreignTotal != null && (
                      <Typography variant="caption" display="block" color="text.secondary" data-testid="bill-foreign-total">{formatFc(bill.currencyCode!, bill.foreignTotal)}</Typography>
                    )}
                    </TableCell>
                    <TableCell>{formatMoney(bill.paidAmount)}
                    {isForeignCurrency(bill.currencyCode) && bill.foreignPaid != null && (
                      <Typography variant="caption" display="block" color="text.secondary" data-testid="bill-foreign-paid">{formatFc(bill.currencyCode!, bill.foreignPaid)}</Typography>
                    )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View" arrow>
                        <IconButton
                          data-help="procurement.billList.action.view"
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/procurement/supplier-bills/${bill.id}`)}
                          aria-label="View"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pageInfo.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pageInfo.totalPages}
                page={pageInfo.currentPage + 1}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SupplierBillList;
